/**
 * Faraid Calculation Engine
 * Pure utility functions for Islamic inheritance calculation
 * 
 * Reference: FaraidInfo.md - K-Faraid MAIS (Majlis Agama Islam Selangor)
 * 
 * This calculator implements the rules of Islamic inheritance (Faraid) according to:
 * - Al-Quran (Surah An-Nisa': 11, 12, 176)
 * - Hadith from Sahih Bukhari, Abu Daud, etc.
 * - Ijma' Ulama (scholarly consensus)
 * 
 * Key Concepts:
 * - Fardu: Fixed shares prescribed in Quran (1/2, 1/3, 1/4, 2/3, 1/6, 1/8)
 * - Asabah: Residual shares (takes remainder after Fardu)
 * - Hijab: Blocking rules (some heirs block others)
 * - 'Aul: Adjustment when shares exceed 100%
 * - Radd: Return of remainder to eligible heirs when shares < 100%
 */

import type {
  DeceasedInfo,
  HeirInput,
  HeirType,
  ShareResult,
  ShareType,
  FixedShareFraction,
  FaraidCalculationResult,
  SpecialCaseInfo,
} from '../types';
import { HEIR_DEFINITIONS } from '../types/heirDefinitions';

// ============================================================================
// Helper Types
// ============================================================================

interface FractionValue {
  numerator: number;
  denominator: number;
}

interface HeirShareCalc {
  heirType: HeirType;
  count: number;
  shareType: ShareType;
  fixedShare?: FixedShareFraction;
  fraction: FractionValue;
  isBlocked: boolean;
  blockedByHeirs: HeirType[];
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate GCD (Greatest Common Divisor) using Euclidean algorithm
 */
function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const temp = b;
    b = a % b;
    a = temp;
  }
  return a;
}

/**
 * Calculate LCM (Least Common Multiple)
 */
function lcm(a: number, b: number): number {
  return Math.abs(a * b) / gcd(a, b);
}

/**
 * Calculate LCM for an array of numbers
 */
function lcmArray(numbers: number[]): number {
  if (numbers.length === 0) return 1;
  return numbers.reduce((a, b) => lcm(a, b), numbers[0]);
}

/**
 * Check if heir is present and has count > 0
 */
function isHeirPresent(heirs: Record<HeirType, HeirInput>, type: HeirType): boolean {
  return heirs[type]?.isPresent && heirs[type]?.count > 0;
}

/**
 * Get heir count
 */
function getHeirCount(heirs: Record<HeirType, HeirInput>, type: HeirType): number {
  return heirs[type]?.count || 0;
}

/**
 * Check if deceased has any descendants (children or grandchildren)
 */
function hasDescendants(heirs: Record<HeirType, HeirInput>): boolean {
  return (
    isHeirPresent(heirs, 'anak_lelaki') ||
    isHeirPresent(heirs, 'anak_perempuan') ||
    isHeirPresent(heirs, 'cucu_lelaki') ||
    isHeirPresent(heirs, 'cucu_perempuan') ||
    isHeirPresent(heirs, 'cicit_lelaki') ||
    isHeirPresent(heirs, 'cicit_perempuan')
  );
}

/**
 * Check if deceased has male descendants
 */
function hasMaleDescendants(heirs: Record<HeirType, HeirInput>): boolean {
  return (
    isHeirPresent(heirs, 'anak_lelaki') ||
    isHeirPresent(heirs, 'cucu_lelaki') ||
    isHeirPresent(heirs, 'cicit_lelaki')
  );
}

/**
 * Check if deceased has multiple siblings (berbilang saudara)
 * This affects mother's share
 */
function hasMultipleSiblings(heirs: Record<HeirType, HeirInput>): boolean {
  let totalSiblings = 0;
  
  const siblingTypes: HeirType[] = [
    'saudara_seibu_sebapa_lelaki',
    'saudara_seibu_sebapa_perempuan',
    'saudara_sebapa_lelaki',
    'saudara_sebapa_perempuan',
    'saudara_seibu_lelaki',
    'saudara_seibu_perempuan',
  ];
  
  for (const type of siblingTypes) {
    if (isHeirPresent(heirs, type)) {
      totalSiblings += getHeirCount(heirs, type);
    }
  }
  
  return totalSiblings >= 2;
}

// ============================================================================
// Blocking (Hijab) Logic
// ============================================================================

/**
 * Determine which heirs are blocked and by whom
 * Implements Hijab Hirman (complete blocking) rules
 * Reference: FaraidInfo.md Section 5.2
 */
function determineBlockedHeirs(
  heirs: Record<HeirType, HeirInput>,
  presentHeirs: HeirType[]
): Map<HeirType, HeirType[]> {
  const blockedMap = new Map<HeirType, HeirType[]>();
  
  for (const heirType of presentHeirs) {
    const definition = HEIR_DEFINITIONS[heirType];
    const blockers: HeirType[] = [];
    
    for (const blockerType of definition.blockedBy) {
      if (isHeirPresent(heirs, blockerType)) {
        blockers.push(blockerType);
      }
    }
    
    if (blockers.length > 0) {
      blockedMap.set(heirType, blockers);
    }
  }
  
  // Special blocking rules for sisters
  // Full sisters (SIBP) get 2/3 together, so paternal sisters (SBP) may get 1/6 or be blocked
  if (isHeirPresent(heirs, 'saudara_sebapa_perempuan')) {
    const sibpCount = getHeirCount(heirs, 'saudara_seibu_sebapa_perempuan');
    
    // If there are 2+ full sisters taking 2/3, paternal sisters are blocked unless...
    if (sibpCount >= 2 && !isHeirPresent(heirs, 'saudara_sebapa_lelaki')) {
      // 2+ SIBP taking 2/3 blocks SBP completely
      const existingBlockers = blockedMap.get('saudara_sebapa_perempuan') || [];
      if (!existingBlockers.includes('saudara_seibu_sebapa_perempuan')) {
        existingBlockers.push('saudara_seibu_sebapa_perempuan');
        blockedMap.set('saudara_sebapa_perempuan', existingBlockers);
      }
    }
  }
  
  return blockedMap;
}

// ============================================================================
// Share Calculation Logic
// ============================================================================

/**
 * Calculate husband's share
 * Reference: FaraidInfo.md Section 4.1.1 - Satu Perdua and Satu Perempat
 * 
 * Rules:
 * - 1/2 if no descendants
 * - 1/4 if has descendants
 */
function calculateHusbandShare(
  heirs: Record<HeirType, HeirInput>,
  deceased: DeceasedInfo
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'suami')) return null;
  if (deceased.gender !== 'perempuan') return null; // Husband only inherits from deceased wife
  
  const hasChildren = hasDescendants(heirs);
  
  return {
    heirType: 'suami',
    count: 1,
    shareType: 'fardu',
    fixedShare: hasChildren ? '1/4' : '1/2',
    fraction: hasChildren 
      ? { numerator: 1, denominator: 4 }
      : { numerator: 1, denominator: 2 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate wife/wives share
 * Reference: FaraidInfo.md Section 4.1.1 - Satu Perempat and Satu Perlapan
 * 
 * Rules:
 * - 1/4 if no descendants (shared among wives)
 * - 1/8 if has descendants (shared among wives)
 */
function calculateWifeShare(
  heirs: Record<HeirType, HeirInput>,
  deceased: DeceasedInfo
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'isteri')) return null;
  if (deceased.gender !== 'lelaki') return null; // Wife only inherits from deceased husband
  
  const hasChildren = hasDescendants(heirs);
  const wifeCount = getHeirCount(heirs, 'isteri');
  
  return {
    heirType: 'isteri',
    count: wifeCount,
    shareType: 'fardu',
    fixedShare: hasChildren ? '1/8' : '1/4',
    fraction: hasChildren
      ? { numerator: 1, denominator: 8 }
      : { numerator: 1, denominator: 4 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate father's share
 * Reference: FaraidInfo.md Section 4.1.1 - Satu Perenam
 * 
 * Rules:
 * - 1/6 fardu + asabah if has descendants
 * - Asabah only if no descendants
 */
function calculateFatherShare(
  heirs: Record<HeirType, HeirInput>
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'bapa')) return null;
  
  const hasChildren = hasDescendants(heirs);
  const hasMaleChildren = hasMaleDescendants(heirs);
  
  // If male descendants exist, father gets only 1/6 fardu
  if (hasMaleChildren) {
    return {
      heirType: 'bapa',
      count: 1,
      shareType: 'fardu',
      fixedShare: '1/6',
      fraction: { numerator: 1, denominator: 6 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // If only female descendants, father gets 1/6 fardu + remaining as asabah
  // This is handled in the asabah section
  if (hasChildren) {
    return {
      heirType: 'bapa',
      count: 1,
      shareType: 'fardu', // Will also get asabah residual
      fixedShare: '1/6',
      fraction: { numerator: 1, denominator: 6 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // No descendants - father inherits as asabah
  return {
    heirType: 'bapa',
    count: 1,
    shareType: 'asabah',
    fraction: { numerator: 0, denominator: 1 }, // Will be calculated later
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate mother's share
 * Reference: FaraidInfo.md Section 4.1.1 - Satu Pertiga and Satu Perenam
 * 
 * Rules:
 * - 1/3 if no descendants AND no multiple siblings
 * - 1/6 if has descendants OR has multiple siblings
 * - 1/3 of remainder in 'Umariyyatain case
 */
function calculateMotherShare(
  heirs: Record<HeirType, HeirInput>,
  _deceased: DeceasedInfo
): { share: HeirShareCalc | null; isUmariyyatain: boolean } {
  if (!isHeirPresent(heirs, 'ibu')) {
    return { share: null, isUmariyyatain: false };
  }
  
  const hasChildren = hasDescendants(heirs);
  const multipleSiblings = hasMultipleSiblings(heirs);
  
  // Check for 'Umariyyatain case
  // Reference: FaraidInfo.md Section 7.2
  // Case: ibu + bapa + (suami OR isteri), no children, no siblings
  const hasFather = isHeirPresent(heirs, 'bapa');
  const hasSpouse = isHeirPresent(heirs, 'suami') || isHeirPresent(heirs, 'isteri');
  
  if (!hasChildren && hasFather && hasSpouse && !multipleSiblings) {
    // 'Umariyyatain - mother gets 1/3 of remainder (after spouse's share)
    // This is 1/3 baki
    return {
      share: {
        heirType: 'ibu',
        count: 1,
        shareType: 'fardu',
        fixedShare: '1/3_baki',
        fraction: { numerator: 1, denominator: 3 }, // Of remainder
        isBlocked: false,
        blockedByHeirs: [],
      },
      isUmariyyatain: true,
    };
  }
  
  // Regular case
  if (hasChildren || multipleSiblings) {
    return {
      share: {
        heirType: 'ibu',
        count: 1,
        shareType: 'fardu',
        fixedShare: '1/6',
        fraction: { numerator: 1, denominator: 6 },
        isBlocked: false,
        blockedByHeirs: [],
      },
      isUmariyyatain: false,
    };
  }
  
  return {
    share: {
      heirType: 'ibu',
      count: 1,
      shareType: 'fardu',
      fixedShare: '1/3',
      fraction: { numerator: 1, denominator: 3 },
      isBlocked: false,
      blockedByHeirs: [],
    },
    isUmariyyatain: false,
  };
}

/**
 * Calculate daughter(s) share
 * Reference: FaraidInfo.md Section 4.1.1 - Satu Perdua and Dua Pertiga
 * 
 * Rules:
 * - Asabah bi al-ghairi if son exists (share with son 1:2 ratio)
 * - 1/2 fardu if 1 daughter only, no son
 * - 2/3 fardu if 2+ daughters, no son
 */
function calculateDaughterShare(
  heirs: Record<HeirType, HeirInput>
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'anak_perempuan')) return null;
  
  const hasSon = isHeirPresent(heirs, 'anak_lelaki');
  const daughterCount = getHeirCount(heirs, 'anak_perempuan');
  
  if (hasSon) {
    // Asabah bi al-ghairi - share with son
    return {
      heirType: 'anak_perempuan',
      count: daughterCount,
      shareType: 'asabah',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // Fardu share
  if (daughterCount === 1) {
    return {
      heirType: 'anak_perempuan',
      count: 1,
      shareType: 'fardu',
      fixedShare: '1/2',
      fraction: { numerator: 1, denominator: 2 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // 2+ daughters
  return {
    heirType: 'anak_perempuan',
    count: daughterCount,
    shareType: 'fardu',
    fixedShare: '2/3',
    fraction: { numerator: 2, denominator: 3 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate son(s) share - always asabah
 * Reference: FaraidInfo.md Section 4.2.1 - Asabah Bi al-Nafsi
 */
function calculateSonShare(
  heirs: Record<HeirType, HeirInput>
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'anak_lelaki')) return null;
  
  return {
    heirType: 'anak_lelaki',
    count: getHeirCount(heirs, 'anak_lelaki'),
    shareType: 'asabah',
    fraction: { numerator: 0, denominator: 1 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate granddaughter(s) share
 * Reference: FaraidInfo.md Section 4.1.1 - Satu Perenam
 * 
 * Rules:
 * - Blocked by son
 * - 1/2 if alone, no grandson, no daughter
 * - 2/3 if 2+ granddaughters, no grandson, no daughter
 * - 1/6 if 1 daughter exists (completing 2/3)
 * - Asabah with grandson
 */
function calculateGranddaughterShare(
  heirs: Record<HeirType, HeirInput>,
  blockedMap: Map<HeirType, HeirType[]>
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'cucu_perempuan')) return null;
  
  const blockers = blockedMap.get('cucu_perempuan');
  if (blockers && blockers.length > 0) {
    return {
      heirType: 'cucu_perempuan',
      count: getHeirCount(heirs, 'cucu_perempuan'),
      shareType: 'mahjub',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: true,
      blockedByHeirs: blockers,
    };
  }
  
  const hasGrandson = isHeirPresent(heirs, 'cucu_lelaki');
  const daughterCount = getHeirCount(heirs, 'anak_perempuan');
  const granddaughterCount = getHeirCount(heirs, 'cucu_perempuan');
  
  // Asabah with grandson
  if (hasGrandson) {
    return {
      heirType: 'cucu_perempuan',
      count: granddaughterCount,
      shareType: 'asabah',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // 1 daughter exists - granddaughter gets 1/6
  if (daughterCount === 1) {
    return {
      heirType: 'cucu_perempuan',
      count: granddaughterCount,
      shareType: 'fardu',
      fixedShare: '1/6',
      fraction: { numerator: 1, denominator: 6 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // 2+ daughters - granddaughter blocked (2/3 already taken)
  if (daughterCount >= 2) {
    return {
      heirType: 'cucu_perempuan',
      count: granddaughterCount,
      shareType: 'mahjub',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: true,
      blockedByHeirs: ['anak_perempuan'],
    };
  }
  
  // No daughter
  if (granddaughterCount === 1) {
    return {
      heirType: 'cucu_perempuan',
      count: 1,
      shareType: 'fardu',
      fixedShare: '1/2',
      fraction: { numerator: 1, denominator: 2 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  return {
    heirType: 'cucu_perempuan',
    count: granddaughterCount,
    shareType: 'fardu',
    fixedShare: '2/3',
    fraction: { numerator: 2, denominator: 3 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate grandson share - asabah if not blocked
 */
function calculateGrandsonShare(
  heirs: Record<HeirType, HeirInput>,
  blockedMap: Map<HeirType, HeirType[]>
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'cucu_lelaki')) return null;
  
  const blockers = blockedMap.get('cucu_lelaki');
  if (blockers && blockers.length > 0) {
    return {
      heirType: 'cucu_lelaki',
      count: getHeirCount(heirs, 'cucu_lelaki'),
      shareType: 'mahjub',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: true,
      blockedByHeirs: blockers,
    };
  }
  
  return {
    heirType: 'cucu_lelaki',
    count: getHeirCount(heirs, 'cucu_lelaki'),
    shareType: 'asabah',
    fraction: { numerator: 0, denominator: 1 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate grandfather (datuk) share
 * Reference: FaraidInfo.md Section 4.1.1 and Section 7.1
 */
function calculateGrandfatherShare(
  heirs: Record<HeirType, HeirInput>,
  blockedMap: Map<HeirType, HeirType[]>
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'datuk')) return null;
  
  const blockers = blockedMap.get('datuk');
  if (blockers && blockers.length > 0) {
    return {
      heirType: 'datuk',
      count: 1,
      shareType: 'mahjub',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: true,
      blockedByHeirs: blockers,
    };
  }
  
  const hasChildren = hasDescendants(heirs);
  const hasMaleChildren = hasMaleDescendants(heirs);
  
  // Similar to father's rules
  if (hasMaleChildren) {
    return {
      heirType: 'datuk',
      count: 1,
      shareType: 'fardu',
      fixedShare: '1/6',
      fraction: { numerator: 1, denominator: 6 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  if (hasChildren) {
    // Female descendants only - 1/6 fardu + asabah
    return {
      heirType: 'datuk',
      count: 1,
      shareType: 'fardu',
      fixedShare: '1/6',
      fraction: { numerator: 1, denominator: 6 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // No descendants - asabah
  return {
    heirType: 'datuk',
    count: 1,
    shareType: 'asabah',
    fraction: { numerator: 0, denominator: 1 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate grandmother share
 * Reference: FaraidInfo.md Section 4.1.1 - Satu Perenam
 */
function calculateGrandmotherShare(
  heirs: Record<HeirType, HeirInput>,
  blockedMap: Map<HeirType, HeirType[]>
): HeirShareCalc[] {
  const results: HeirShareCalc[] = [];
  
  // Maternal grandmother
  if (isHeirPresent(heirs, 'nenek_sebelah_ibu')) {
    const blockers = blockedMap.get('nenek_sebelah_ibu');
    if (blockers && blockers.length > 0) {
      results.push({
        heirType: 'nenek_sebelah_ibu',
        count: 1,
        shareType: 'mahjub',
        fraction: { numerator: 0, denominator: 1 },
        isBlocked: true,
        blockedByHeirs: blockers,
      });
    } else {
      results.push({
        heirType: 'nenek_sebelah_ibu',
        count: 1,
        shareType: 'fardu',
        fixedShare: '1/6',
        fraction: { numerator: 1, denominator: 6 },
        isBlocked: false,
        blockedByHeirs: [],
      });
    }
  }
  
  // Paternal grandmother
  if (isHeirPresent(heirs, 'nenek_sebelah_bapa')) {
    const blockers = blockedMap.get('nenek_sebelah_bapa');
    if (blockers && blockers.length > 0) {
      results.push({
        heirType: 'nenek_sebelah_bapa',
        count: 1,
        shareType: 'mahjub',
        fraction: { numerator: 0, denominator: 1 },
        isBlocked: true,
        blockedByHeirs: blockers,
      });
    } else {
      // If maternal grandmother also present, they share 1/6
      const maternalPresent = isHeirPresent(heirs, 'nenek_sebelah_ibu') && 
        !blockedMap.has('nenek_sebelah_ibu');
      
      if (maternalPresent) {
        // Already counted in maternal grandmother's share
        results.push({
          heirType: 'nenek_sebelah_bapa',
          count: 1,
          shareType: 'fardu',
          fixedShare: '1/6',
          fraction: { numerator: 0, denominator: 1 }, // Shares with maternal grandmother
          isBlocked: false,
          blockedByHeirs: [],
        });
      } else {
        results.push({
          heirType: 'nenek_sebelah_bapa',
          count: 1,
          shareType: 'fardu',
          fixedShare: '1/6',
          fraction: { numerator: 1, denominator: 6 },
          isBlocked: false,
          blockedByHeirs: [],
        });
      }
    }
  }
  
  return results;
}

/**
 * Calculate full sister (SIBP) share
 * Reference: FaraidInfo.md Section 4.1.1 and Section 4.2.3
 */
function calculateFullSisterShare(
  heirs: Record<HeirType, HeirInput>,
  blockedMap: Map<HeirType, HeirType[]>
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'saudara_seibu_sebapa_perempuan')) return null;
  
  const blockers = blockedMap.get('saudara_seibu_sebapa_perempuan');
  if (blockers && blockers.length > 0) {
    return {
      heirType: 'saudara_seibu_sebapa_perempuan',
      count: getHeirCount(heirs, 'saudara_seibu_sebapa_perempuan'),
      shareType: 'mahjub',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: true,
      blockedByHeirs: blockers,
    };
  }
  
  const hasFullBrother = isHeirPresent(heirs, 'saudara_seibu_sebapa_lelaki');
  const sisterCount = getHeirCount(heirs, 'saudara_seibu_sebapa_perempuan');
  
  // Asabah ma'a al-ghairi (with daughters/granddaughters)
  const hasDaughterOnly = isHeirPresent(heirs, 'anak_perempuan') && !isHeirPresent(heirs, 'anak_lelaki');
  const hasGranddaughterOnly = isHeirPresent(heirs, 'cucu_perempuan') && !isHeirPresent(heirs, 'cucu_lelaki');
  
  if (!hasFullBrother && (hasDaughterOnly || hasGranddaughterOnly)) {
    return {
      heirType: 'saudara_seibu_sebapa_perempuan',
      count: sisterCount,
      shareType: 'asabah',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // Asabah bi al-ghairi (with full brother)
  if (hasFullBrother) {
    return {
      heirType: 'saudara_seibu_sebapa_perempuan',
      count: sisterCount,
      shareType: 'asabah',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // Fardu share
  if (sisterCount === 1) {
    return {
      heirType: 'saudara_seibu_sebapa_perempuan',
      count: 1,
      shareType: 'fardu',
      fixedShare: '1/2',
      fraction: { numerator: 1, denominator: 2 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  return {
    heirType: 'saudara_seibu_sebapa_perempuan',
    count: sisterCount,
    shareType: 'fardu',
    fixedShare: '2/3',
    fraction: { numerator: 2, denominator: 3 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate full brother (SIBL) share - asabah
 */
function calculateFullBrotherShare(
  heirs: Record<HeirType, HeirInput>,
  blockedMap: Map<HeirType, HeirType[]>
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'saudara_seibu_sebapa_lelaki')) return null;
  
  const blockers = blockedMap.get('saudara_seibu_sebapa_lelaki');
  if (blockers && blockers.length > 0) {
    return {
      heirType: 'saudara_seibu_sebapa_lelaki',
      count: getHeirCount(heirs, 'saudara_seibu_sebapa_lelaki'),
      shareType: 'mahjub',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: true,
      blockedByHeirs: blockers,
    };
  }
  
  return {
    heirType: 'saudara_seibu_sebapa_lelaki',
    count: getHeirCount(heirs, 'saudara_seibu_sebapa_lelaki'),
    shareType: 'asabah',
    fraction: { numerator: 0, denominator: 1 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate paternal half-sister (SBP) share
 * Similar to full sister but with additional blocking rules
 */
function calculatePaternalSisterShare(
  heirs: Record<HeirType, HeirInput>,
  blockedMap: Map<HeirType, HeirType[]>
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'saudara_sebapa_perempuan')) return null;
  
  const blockers = blockedMap.get('saudara_sebapa_perempuan');
  if (blockers && blockers.length > 0) {
    return {
      heirType: 'saudara_sebapa_perempuan',
      count: getHeirCount(heirs, 'saudara_sebapa_perempuan'),
      shareType: 'mahjub',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: true,
      blockedByHeirs: blockers,
    };
  }
  
  const hasPaternalBrother = isHeirPresent(heirs, 'saudara_sebapa_lelaki');
  const fullSisterCount = getHeirCount(heirs, 'saudara_seibu_sebapa_perempuan');
  const sisterCount = getHeirCount(heirs, 'saudara_sebapa_perempuan');
  
  // Asabah with paternal brother
  if (hasPaternalBrother) {
    return {
      heirType: 'saudara_sebapa_perempuan',
      count: sisterCount,
      shareType: 'asabah',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // 1 full sister present - paternal sister gets 1/6
  if (fullSisterCount === 1) {
    return {
      heirType: 'saudara_sebapa_perempuan',
      count: sisterCount,
      shareType: 'fardu',
      fixedShare: '1/6',
      fraction: { numerator: 1, denominator: 6 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  // No full sister
  if (sisterCount === 1) {
    return {
      heirType: 'saudara_sebapa_perempuan',
      count: 1,
      shareType: 'fardu',
      fixedShare: '1/2',
      fraction: { numerator: 1, denominator: 2 },
      isBlocked: false,
      blockedByHeirs: [],
    };
  }
  
  return {
    heirType: 'saudara_sebapa_perempuan',
    count: sisterCount,
    shareType: 'fardu',
    fixedShare: '2/3',
    fraction: { numerator: 2, denominator: 3 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate paternal half-brother (SBL) share
 */
function calculatePaternalBrotherShare(
  heirs: Record<HeirType, HeirInput>,
  blockedMap: Map<HeirType, HeirType[]>
): HeirShareCalc | null {
  if (!isHeirPresent(heirs, 'saudara_sebapa_lelaki')) return null;
  
  const blockers = blockedMap.get('saudara_sebapa_lelaki');
  if (blockers && blockers.length > 0) {
    return {
      heirType: 'saudara_sebapa_lelaki',
      count: getHeirCount(heirs, 'saudara_sebapa_lelaki'),
      shareType: 'mahjub',
      fraction: { numerator: 0, denominator: 1 },
      isBlocked: true,
      blockedByHeirs: blockers,
    };
  }
  
  return {
    heirType: 'saudara_sebapa_lelaki',
    count: getHeirCount(heirs, 'saudara_sebapa_lelaki'),
    shareType: 'asabah',
    fraction: { numerator: 0, denominator: 1 },
    isBlocked: false,
    blockedByHeirs: [],
  };
}

/**
 * Calculate maternal siblings (seibu) share
 * Reference: FaraidInfo.md Section 4.1.1 - Satu Perenam and Satu Pertiga
 * 
 * Rules:
 * - Equal share between males and females (unique to maternal siblings)
 * - 1/6 for one, 1/3 shared for 2+
 */
function calculateMaternalSiblingsShare(
  heirs: Record<HeirType, HeirInput>,
  blockedMap: Map<HeirType, HeirType[]>
): HeirShareCalc[] {
  const results: HeirShareCalc[] = [];
  
  const hasMaternalBrother = isHeirPresent(heirs, 'saudara_seibu_lelaki');
  const hasMaternalSister = isHeirPresent(heirs, 'saudara_seibu_perempuan');
  
  if (!hasMaternalBrother && !hasMaternalSister) return results;
  
  // Check if blocked
  const brotherBlockers = blockedMap.get('saudara_seibu_lelaki');
  const sisterBlockers = blockedMap.get('saudara_seibu_perempuan');
  
  const totalCount = 
    getHeirCount(heirs, 'saudara_seibu_lelaki') + 
    getHeirCount(heirs, 'saudara_seibu_perempuan');
  
  // Maternal siblings share equally (unique rule)
  const share: FixedShareFraction = totalCount === 1 ? '1/6' : '1/3';
  const fraction = totalCount === 1 
    ? { numerator: 1, denominator: 6 }
    : { numerator: 1, denominator: 3 };
  
  if (hasMaternalBrother) {
    if (brotherBlockers && brotherBlockers.length > 0) {
      results.push({
        heirType: 'saudara_seibu_lelaki',
        count: getHeirCount(heirs, 'saudara_seibu_lelaki'),
        shareType: 'mahjub',
        fraction: { numerator: 0, denominator: 1 },
        isBlocked: true,
        blockedByHeirs: brotherBlockers,
      });
    } else {
      results.push({
        heirType: 'saudara_seibu_lelaki',
        count: getHeirCount(heirs, 'saudara_seibu_lelaki'),
        shareType: 'fardu',
        fixedShare: share,
        fraction: hasMaternalSister ? { numerator: 0, denominator: 1 } : fraction,
        isBlocked: false,
        blockedByHeirs: [],
      });
    }
  }
  
  if (hasMaternalSister) {
    if (sisterBlockers && sisterBlockers.length > 0) {
      results.push({
        heirType: 'saudara_seibu_perempuan',
        count: getHeirCount(heirs, 'saudara_seibu_perempuan'),
        shareType: 'mahjub',
        fraction: { numerator: 0, denominator: 1 },
        isBlocked: true,
        blockedByHeirs: sisterBlockers,
      });
    } else {
      results.push({
        heirType: 'saudara_seibu_perempuan',
        count: getHeirCount(heirs, 'saudara_seibu_perempuan'),
        shareType: 'fardu',
        fixedShare: share,
        fraction: fraction, // Main share holder
        isBlocked: false,
        blockedByHeirs: [],
      });
    }
  }
  
  return results;
}

/**
 * Calculate asabah heirs (nephews, uncles, cousins)
 * These inherit residually and are blocked in hierarchical order
 */
function calculateAsabahRelatives(
  heirs: Record<HeirType, HeirInput>,
  blockedMap: Map<HeirType, HeirType[]>
): HeirShareCalc[] {
  const results: HeirShareCalc[] = [];
  
  const asabahTypes: HeirType[] = [
    'anak_saudara_seibu_sebapa_lelaki',
    'anak_saudara_sebapa_lelaki',
    'bapa_saudara_seibu_sebapa',
    'bapa_saudara_sebapa',
    'anak_bapa_saudara_seibu_sebapa',
    'anak_bapa_saudara_sebapa',
    'datuk_saudara_seibu_sebapa',
    'datuk_saudara_sebapa',
    'anak_datuk_saudara_seibu_sebapa',
    'anak_datuk_saudara_sebapa',
    'cicit_lelaki',
    'cicit_perempuan',
  ];
  
  for (const type of asabahTypes) {
    if (!isHeirPresent(heirs, type)) continue;
    
    const blockers = blockedMap.get(type);
    if (blockers && blockers.length > 0) {
      results.push({
        heirType: type,
        count: getHeirCount(heirs, type),
        shareType: 'mahjub',
        fraction: { numerator: 0, denominator: 1 },
        isBlocked: true,
        blockedByHeirs: blockers,
      });
    } else {
      results.push({
        heirType: type,
        count: getHeirCount(heirs, type),
        shareType: 'asabah',
        fraction: { numerator: 0, denominator: 1 },
        isBlocked: false,
        blockedByHeirs: [],
      });
    }
  }
  
  return results;
}

// ============================================================================
// Main Calculation Functions
// ============================================================================

/**
 * Calculate all heir shares
 */
function calculateAllShares(
  heirs: Record<HeirType, HeirInput>,
  deceased: DeceasedInfo
): { shares: HeirShareCalc[]; isUmariyyatain: boolean } {
  const presentHeirs = Object.entries(heirs)
    .filter(([_, input]) => input.isPresent && input.count > 0)
    .map(([type]) => type as HeirType);
  
  const blockedMap = determineBlockedHeirs(heirs, presentHeirs);
  const shares: HeirShareCalc[] = [];
  let isUmariyyatain = false;
  
  // Calculate spouse shares
  const husbandShare = calculateHusbandShare(heirs, deceased);
  if (husbandShare) shares.push(husbandShare);
  
  const wifeShare = calculateWifeShare(heirs, deceased);
  if (wifeShare) shares.push(wifeShare);
  
  // Calculate parent shares
  const fatherShare = calculateFatherShare(heirs);
  if (fatherShare) shares.push(fatherShare);
  
  const motherResult = calculateMotherShare(heirs, deceased);
  if (motherResult.share) {
    shares.push(motherResult.share);
    isUmariyyatain = motherResult.isUmariyyatain;
  }
  
  // Calculate children shares
  const sonShare = calculateSonShare(heirs);
  if (sonShare) shares.push(sonShare);
  
  const daughterShare = calculateDaughterShare(heirs);
  if (daughterShare) shares.push(daughterShare);
  
  // Calculate grandparent shares
  const grandfatherShare = calculateGrandfatherShare(heirs, blockedMap);
  if (grandfatherShare) shares.push(grandfatherShare);
  
  const grandmotherShares = calculateGrandmotherShare(heirs, blockedMap);
  shares.push(...grandmotherShares);
  
  // Calculate grandchildren shares
  const grandsonShare = calculateGrandsonShare(heirs, blockedMap);
  if (grandsonShare) shares.push(grandsonShare);
  
  const granddaughterShare = calculateGranddaughterShare(heirs, blockedMap);
  if (granddaughterShare) shares.push(granddaughterShare);
  
  // Calculate sibling shares
  const fullBrotherShare = calculateFullBrotherShare(heirs, blockedMap);
  if (fullBrotherShare) shares.push(fullBrotherShare);
  
  const fullSisterShare = calculateFullSisterShare(heirs, blockedMap);
  if (fullSisterShare) shares.push(fullSisterShare);
  
  const paternalBrotherShare = calculatePaternalBrotherShare(heirs, blockedMap);
  if (paternalBrotherShare) shares.push(paternalBrotherShare);
  
  const paternalSisterShare = calculatePaternalSisterShare(heirs, blockedMap);
  if (paternalSisterShare) shares.push(paternalSisterShare);
  
  const maternalSiblingsShares = calculateMaternalSiblingsShare(heirs, blockedMap);
  shares.push(...maternalSiblingsShares);
  
  // Calculate other asabah relatives
  const asabahRelatives = calculateAsabahRelatives(heirs, blockedMap);
  shares.push(...asabahRelatives);
  
  return { shares, isUmariyyatain };
}

/**
 * Calculate base denominator (Asal Masalah)
 * This is the LCM of all fardu share denominators
 */
function calculateBaseDenominator(shares: HeirShareCalc[]): number {
  const farduDenominators = shares
    .filter(s => s.shareType === 'fardu' && !s.isBlocked && s.fraction.denominator > 0)
    .map(s => s.fraction.denominator);
  
  if (farduDenominators.length === 0) return 1;
  
  return lcmArray(farduDenominators);
}

/**
 * Calculate total fardu shares
 */
function calculateTotalFarduShares(shares: HeirShareCalc[], baseDenominator: number): number {
  let total = 0;
  
  for (const share of shares) {
    if (share.shareType === 'fardu' && !share.isBlocked && share.fraction.numerator > 0) {
      const normalizedNumerator = (share.fraction.numerator * baseDenominator) / share.fraction.denominator;
      total += normalizedNumerator;
    }
  }
  
  return total;
}

/**
 * Calculate asabah distribution
 */
function calculateAsabahDistribution(
  shares: HeirShareCalc[],
  baseDenominator: number,
  totalFarduShares: number
): { asabahShares: Map<HeirType, number>; hasAsabah: boolean; scalingFactor: number } {
  const asabahShares = new Map<HeirType, number>();
  const remainder = baseDenominator - totalFarduShares;
  
  if (remainder <= 0) {
    return { asabahShares, hasAsabah: false, scalingFactor: 1 };
  }
  
  // Get non-blocked asabah heirs in priority order
  const asabahHeirs = shares.filter(s => s.shareType === 'asabah' && !s.isBlocked);
  
  if (asabahHeirs.length === 0) {
    return { asabahShares, hasAsabah: false, scalingFactor: 1 };
  }
  
  // Distribute among asabah heirs
  // Calculate total "units" (sons count as 2 units, daughters as 1)
  let totalUnits = 0;
  const heirUnits = new Map<HeirType, number>();
  
  // Check if we have son + daughter combination (asabah bi al-ghairi)
  const hasSon = asabahHeirs.some(h => h.heirType === 'anak_lelaki');
  const hasDaughter = asabahHeirs.some(h => h.heirType === 'anak_perempuan');
  const hasGrandson = asabahHeirs.some(h => h.heirType === 'cucu_lelaki');
  
  for (const heir of asabahHeirs) {
    let units = heir.count;
    
    // Apply 2:1 ratio for male:female
    const def = HEIR_DEFINITIONS[heir.heirType];
    if (def.gender === 'lelaki') {
      units = heir.count * 2;
    }
    
    // Special case: daughters only with father as asabah
    // Father takes remainder after daughters
    if (heir.heirType === 'bapa' && (hasSon || hasDaughter)) {
      continue;
    }
    
    // Only first asabah in hierarchy gets share
    if (heirUnits.size === 0 || 
        (hasSon && (heir.heirType === 'anak_lelaki' || heir.heirType === 'anak_perempuan')) ||
        (hasGrandson && (heir.heirType === 'cucu_lelaki' || heir.heirType === 'cucu_perempuan'))) {
      heirUnits.set(heir.heirType, units);
      totalUnits += units;
    }
  }
  
  if (totalUnits === 0) {
    return { asabahShares, hasAsabah: false, scalingFactor: 1 };
  }
  
  // Scale denominator so remainder divides evenly among asabah units
  const scalingFactor = remainder % totalUnits === 0
    ? 1
    : totalUnits / gcd(remainder, totalUnits);
  const scaledRemainder = remainder * scalingFactor;
  
  for (const [type, units] of heirUnits) {
    const shareAmount = (scaledRemainder * units) / totalUnits;
    asabahShares.set(type, shareAmount);
  }
  
  return { asabahShares, hasAsabah: heirUnits.size > 0, scalingFactor };
}

/**
 * Apply 'Aul adjustment if shares exceed base
 * Reference: FaraidInfo.md Section 6
 */
function applyAul(
  _shares: HeirShareCalc[],
  baseDenominator: number,
  totalFarduShares: number
): { adjustedDenominator: number; isAul: boolean } {
  if (totalFarduShares <= baseDenominator) {
    return { adjustedDenominator: baseDenominator, isAul: false };
  }
  
  // 'Aul: increase denominator to match total shares
  return { adjustedDenominator: totalFarduShares, isAul: true };
}

/**
 * Main calculation function
 * Calculates faraid distribution for given heirs and estate
 */
export function calculateFaraid(
  heirs: Record<HeirType, HeirInput>,
  deceased: DeceasedInfo,
  totalEstate: number
): FaraidCalculationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Validate inputs
  if (totalEstate <= 0) {
    errors.push('Jumlah harta pusaka mestilah lebih daripada sifar');
  }
  
  const presentHeirs = Object.entries(heirs)
    .filter(([_, input]) => input.isPresent && input.count > 0);
  
  // Check for invalid spouse combination
  if (deceased.gender === 'lelaki' && isHeirPresent(heirs, 'suami')) {
    errors.push('Si mati lelaki tidak boleh mempunyai suami sebagai waris');
  }
  if (deceased.gender === 'perempuan' && isHeirPresent(heirs, 'isteri')) {
    errors.push('Si mati perempuan tidak boleh mempunyai isteri sebagai waris');
  }

  // No heirs → all estate goes to Baitulmal (per MAIS reference, Section 10)
  if (presentHeirs.length === 0) {
    return {
      totalEstate,
      baseDenominator: 1,
      finalDenominator: 1,
      shares: [],
      totalDistributed: 0,
      remainderToBaitulmal: totalEstate,
      specialCases: {
        isAul: false,
        isRadd: false,
        isUmariyyatain: false,
        isMusytarikah: false,
        isDatukWithSiblings: false,
      },
      errors,
      warnings,
      isValid: errors.length === 0,
    };
  }

  if (errors.length > 0) {
    return {
      totalEstate,
      baseDenominator: 0,
      finalDenominator: 0,
      shares: [],
      totalDistributed: 0,
      remainderToBaitulmal: 0,
      specialCases: {
        isAul: false,
        isRadd: false,
        isUmariyyatain: false,
        isMusytarikah: false,
        isDatukWithSiblings: false,
      },
      errors,
      warnings,
      isValid: false,
    };
  }
  
  // Calculate shares
  const { shares, isUmariyyatain } = calculateAllShares(heirs, deceased);
  
  // Calculate base denominator
  const baseDenominator = calculateBaseDenominator(shares);
  
  // Handle 'Umariyyatain case
  // Mother gets 1/3 of remainder (after spouse's share)
  if (isUmariyyatain) {
    const spouseShare = shares.find(s => s.heirType === 'suami' || s.heirType === 'isteri');
    if (spouseShare) {
      const motherShare = shares.find(s => s.heirType === 'ibu');
      if (motherShare) {
        // Update mother's fraction for display (1/3 of remainder after spouse)
        if (spouseShare.heirType === 'suami') {
          // Husband 1/2, Mother 1/6 of total (1/3 of 1/2 remainder)
          motherShare.fraction = { numerator: 1, denominator: 6 };
        } else {
          // Wife 1/4, Mother 1/4 of total (1/3 of 3/4 remainder)
          motherShare.fraction = { numerator: 1, denominator: 4 };
        }
      }
    }
  }
  
  // Recalculate with updated fractions
  const totalFarduShares = calculateTotalFarduShares(shares, baseDenominator);
  
  // Apply 'Aul if needed
  const { adjustedDenominator, isAul } = applyAul(shares, baseDenominator, totalFarduShares);
  
  // Calculate asabah distribution
  const { asabahShares, hasAsabah, scalingFactor } = calculateAsabahDistribution(
    shares, 
    adjustedDenominator, 
    totalFarduShares
  );
  
  // Apply scaling factor to denominator so asabah shares are whole numbers
  const finalDen = adjustedDenominator * scalingFactor;
  
  // Build final share results
  const finalShares: ShareResult[] = [];
  let totalDistributed = 0;
  
  for (const share of shares) {
    let fractionNum = 0;
    const fractionDen = finalDen;
    
    if (share.isBlocked) {
      fractionNum = 0;
    } else if (share.shareType === 'fardu') {
      if (share.fraction.numerator > 0 && share.fraction.denominator > 0) {
        fractionNum = (share.fraction.numerator * finalDen) / share.fraction.denominator;
      }
    } else if (share.shareType === 'asabah') {
      fractionNum = asabahShares.get(share.heirType) || 0;
    }
    
    const percentage = finalDen > 0 ? (fractionNum / finalDen) * 100 : 0;
    const totalAmount = totalEstate * (fractionNum / finalDen);
    const amountPerHeir = share.count > 0 ? totalAmount / share.count : 0;
    
    totalDistributed += totalAmount;
    
    const def = HEIR_DEFINITIONS[share.heirType];
    
    finalShares.push({
      heirType: share.heirType,
      heirLabel: def.labelMs,
      count: share.count,
      shareType: share.shareType,
      fixedShare: share.fixedShare,
      fractionNumerator: fractionNum,
      fractionDenominator: fractionDen,
      percentage,
      totalAmount,
      amountPerHeir,
      isBlocked: share.isBlocked,
      blockedByHeirs: share.blockedByHeirs,
    });
  }
  
  // Calculate remainder to Baitulmal
  const remainderToBaitulmal = Math.max(0, totalEstate - totalDistributed);
  
  // Check for Radd case (shares < 100% without asabah)
  const isRadd = !hasAsabah && totalDistributed < totalEstate && remainderToBaitulmal > 0;
  
  // Special case detection
  const specialCases: SpecialCaseInfo = {
    isAul,
    aulOriginalDenominator: isAul ? baseDenominator : undefined,
    aulNewDenominator: isAul ? adjustedDenominator : undefined,
    isRadd,
    isUmariyyatain,
    isMusytarikah: false, // TODO: Implement Musytarikah detection
    isDatukWithSiblings: isHeirPresent(heirs, 'datuk') && (
      isHeirPresent(heirs, 'saudara_seibu_sebapa_lelaki') ||
      isHeirPresent(heirs, 'saudara_seibu_sebapa_perempuan') ||
      isHeirPresent(heirs, 'saudara_sebapa_lelaki') ||
      isHeirPresent(heirs, 'saudara_sebapa_perempuan')
    ),
  };
  
  // Add warnings for special cases
  if (isAul) {
    warnings.push(`Masalah 'Aul: Jumlah bahagian fardu melebihi 100%. Bahagian diselaraskan dari ${baseDenominator} kepada ${adjustedDenominator}.`);
  }
  
  if (isRadd) {
    warnings.push('Baki harta akan diserahkan kepada Baitulmal kerana tiada waris asobah.');
  }
  
  if (specialCases.isDatukWithSiblings) {
    warnings.push('Kes khas: Datuk bersama saudara. Pengiraan menggunakan kaedah Zaid bin Thabit r.a.');
  }
  
  return {
    totalEstate,
    baseDenominator,
    finalDenominator: finalDen,
    shares: finalShares,
    totalDistributed,
    remainderToBaitulmal,
    specialCases,
    errors,
    warnings,
    isValid: errors.length === 0,
  };
}

/**
 * Format currency in Malaysian Ringgit
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('ms-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format fraction for display
 */
export function formatFraction(numerator: number, denominator: number): string {
  if (numerator === 0) return '0';
  if (denominator === 1) return numerator.toString();
  
  // Simplify fraction
  const divisor = gcd(numerator, denominator);
  const simpNum = numerator / divisor;
  const simpDen = denominator / divisor;
  
  return `${simpNum}/${simpDen}`;
}

/**
 * Get share type label in Malay
 */
export function getShareTypeLabel(type: ShareType): string {
  const labels: Record<ShareType, string> = {
    fardu: 'Fardu',
    asabah: 'Asobah',
    mahjub: 'Mahjub (Terdinding)',
  };
  return labels[type];
}
